import React, { useState, useEffect } from 'react';
import { Play, Bug, Zap, Target, CheckCircle, XCircle, Code, FileCode } from 'lucide-react';
import { advancedTestingService, TestSuite, DebugSession, CodeQualityMetrics } from '../../services/advancedTestingService';
import { analyzeTextNLP } from '../../services/api';
import { toast } from 'sonner';
import { Brain, TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface TestingPanelProps {
  code: string;
  language: string;
  onTestResults?: (results: TestSuite) => void;
  onDebugSession?: (session: DebugSession) => void;
  onQualityReport?: (metrics: CodeQualityMetrics) => void;
}

export function TestingPanel({
  code,
  language,
  onTestResults,
  onDebugSession,
  onQualityReport
}: TestingPanelProps) {
  const [activeTab, setActiveTab] = useState<'tests' | 'debug' | 'quality' | 'refactor' | 'predict' | 'optimize'>('tests');
  const [isGeneratingTests, setIsGeneratingTests] = useState(false);
  const [testSuite, setTestSuite] = useState<TestSuite | null>(null);
  const [debugSession, setDebugSession] = useState<DebugSession | null>(null);
  const [qualityMetrics, setQualityMetrics] = useState<CodeQualityMetrics | null>(null);
  const [refactorings, setRefactorings] = useState<any>(null);
  const [predictions, setPredictions] = useState<any>(null);
  const [performanceAnalysis, setPerformanceAnalysis] = useState<any>(null);
  const [dependencyAnalysis, setDependencyAnalysis] = useState<any>(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPanelMode, setAiPanelMode] = useState<'generate' | 'analyze' | 'refactor' | 'patterns' | 'document' | 'predict' | 'optimize' | 'dependencies'>('generate');

  const generateTests = async () => {
    setIsGeneratingTests(true);
    try {
      const testCases = await advancedTestingService.generateTests(code, language);
      const suite: TestSuite = {
        id: `suite_${Date.now()}`,
        name: 'AI Generated Test Suite',
        description: 'Automatically generated test suite based on code analysis',
        testCases,
        coverage: { statements: 0, branches: 0, functions: 0, lines: 0 },
        status: 'pending',
        duration: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setTestSuite(suite);
      onTestResults?.(suite);
      toast.success(`Generated ${testCases.length} test cases`);
    } catch (error) {
      toast.error('Failed to generate tests');
    } finally {
      setIsGeneratingTests(false);
    }
  };

  const runTests = async () => {
    if (!testSuite) return;

    try {
      const results = await advancedTestingService.runTestSuite(testSuite.id);
      setTestSuite(results);
      onTestResults?.(results);

      const passed = results.testCases.filter(t => t.assertions.every(a => a.passed)).length;
      const total = results.testCases.length;

      if (passed === total) {
        toast.success(`All ${total} tests passed!`);
      } else {
        toast.warning(`${passed}/${total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run tests');
    }
  };

  const startDebugging = async () => {
    try {
      const session = await advancedTestingService.startDebugSession(code, language);
      setDebugSession(session);
      onDebugSession?.(session);
      toast.success('Debug session started');
    } catch (error) {
      toast.error('Failed to start debug session');
    }
  };

  const analyzeQuality = async () => {
    try {
      const metrics = await advancedTestingService.analyzeCodeQuality(code, language);
      setQualityMetrics(metrics);
      onQualityReport?.(metrics);
      toast.success(`Quality score: ${metrics.overall}/100`);
    } catch (error) {
      toast.error('Failed to analyze code quality');
    }
  };

  const suggestRefactoring = async () => {
    try {
      const suggestions = await advancedTestingService.suggestRefactoring(code, language);
      setRefactorings(suggestions);
      toast.success(`Found ${suggestions.refactorings.length} refactoring opportunities`);
    } catch (error) {
      console.error('Refactoring suggestion failed:', error);
      toast.error('Failed to analyze refactoring opportunities. Please try again.');
    }
  };

  // ===== PHASE 7.2 WEEK 2: ADVANCED AI FEATURES =====

  const handlePredictIssues = async () => {
    try {
      // Use historical data for predictions (would be loaded from backend in production)
      const historicalData = [
        { language, complexity: 5, issues: ['unhandled_promise'], functionCount: 8 },
        { language, complexity: 7, issues: ['array_performance'], functionCount: 12 }
      ];

      const result = await advancedTestingService.predictCodeIssues(code, language, historicalData);
      setPredictions(result);
      setAiPanelMode('predict');
      setShowAIPanel(true);

      toast.success(`Predicted ${result.predictions.length} potential issues`);
    } catch (error) {
      console.error('Issue prediction failed:', error);
      toast.error('Failed to predict issues. Please try again.');
    }
  };

  const handlePerformanceOptimization = async () => {
    try {
      const result = await advancedTestingService.optimizePerformance(code, language);
      setPerformanceAnalysis(result);
      setAiPanelMode('optimize');
      setShowAIPanel(true);

      toast.success(`Found ${result.optimizations.length} optimization opportunities`);
    } catch (error) {
      console.error('Performance optimization failed:', error);
      toast.error('Failed to analyze performance. Please try again.');
    }
  };

  const handleDependencyAnalysis = async () => {
    try {
      // Get project files (would be passed from parent component in production)
      const projectFiles = ['src/main.js', 'src/utils.js', 'package.json'];
      const result = await advancedTestingService.analyzeMultiFileDependencies(code, language, projectFiles);
      setDependencyAnalysis(result);
      setAiPanelMode('dependencies');
      setShowAIPanel(true);

      toast.success(`Analyzed dependencies: ${result.dependencies.length} found`);
    } catch (error) {
      console.error('Dependency analysis failed:', error);
      toast.error('Failed to analyze dependencies. Please try again.');
    }
  };

  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (score >= 60) return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {[
          { id: 'tests', label: 'Tests', icon: Play },
          { id: 'debug', label: 'Debug', icon: Bug },
          { id: 'quality', label: 'Quality', icon: Target },
          { id: 'refactor', label: 'Refactor', icon: Zap },
          { id: 'predict', label: 'Predict', icon: Brain },
          { id: 'optimize', label: 'Optimize', icon: TrendingUp }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'tests' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Test Suite</h3>
              <div className="flex gap-2">
                <button
                  onClick={generateTests}
                  disabled={isGeneratingTests}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                >
                  <Code className="w-4 h-4" />
                  {isGeneratingTests ? 'Generating...' : 'Generate Tests'}
                </button>
                {testSuite && (
                  <button
                    onClick={runTests}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Run Tests
                  </button>
                )}
              </div>
            </div>

            {testSuite && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{testSuite.name}</h4>
                    <span className={`px-2 py-1 text-xs rounded ${
                      testSuite.status === 'passed' ? 'bg-green-100 text-green-800' :
                      testSuite.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {testSuite.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {testSuite.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>Tests: {testSuite.testCases.length}</div>
                    <div>Duration: {testSuite.duration}ms</div>
                    <div>Passed: {testSuite.testCases.filter(t => t.assertions.every(a => a.passed)).length}</div>
                    <div>Coverage: {testSuite.coverage.statements}%</div>
                  </div>
                </div>

                <div className="space-y-2">
                  {testSuite.testCases.map((testCase) => (
                    <div key={testCase.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium">{testCase.name}</h5>
                        <div className="flex gap-1">
                          {testCase.assertions.map((assertion, index) => (
                            assertion.passed ?
                              <CheckCircle key={index} className="w-4 h-4 text-green-500" /> :
                              <XCircle key={index} className="w-4 h-4 text-red-500" />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {testCase.description}
                      </p>
                      {testCase.assertions.some(a => !a.passed) && (
                        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                          {testCase.assertions.find(a => !a.passed)?.message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'debug' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Debug Session</h3>
              <button
                onClick={startDebugging}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center gap-2"
              >
                <Bug className="w-4 h-4" />
                Start Debugging
              </button>
            </div>

            {debugSession && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Debug Session Active</h4>
                    <span className={`px-2 py-1 text-xs rounded ${
                      debugSession.status === 'active' ? 'bg-green-100 text-green-800' :
                      debugSession.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {debugSession.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Current Line: {debugSession.currentLine} | Breakpoints: {debugSession.breakpoints.length}
                  </div>
                </div>

                {debugSession.aiSuggestions.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">AI Debug Suggestions</h4>
                    <div className="space-y-2">
                      {debugSession.aiSuggestions.map((suggestion, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                          <div>
                            <p className="text-sm text-blue-800 dark:text-blue-200">{suggestion.description}</p>
                            <p className="text-xs text-blue-600 dark:text-blue-300">
                              Confidence: {Math.round(suggestion.confidence * 100)}%
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                    <h4 className="font-medium mb-2">Breakpoints</h4>
                    {debugSession.breakpoints.length === 0 ? (
                      <p className="text-sm text-gray-500">No breakpoints set</p>
                    ) : (
                      <div className="space-y-1">
                        {debugSession.breakpoints.map((bp) => (
                          <div key={bp.id} className="text-sm flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${bp.enabled ? 'bg-red-500' : 'bg-gray-400'}`}></div>
                            Line {bp.line}: {bp.condition || 'Always'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                    <h4 className="font-medium mb-2">Variables</h4>
                    {debugSession.variables.length === 0 ? (
                      <p className="text-sm text-gray-500">No variables in scope</p>
                    ) : (
                      <div className="space-y-1">
                        {debugSession.variables.map((variable) => (
                          <div key={variable.name} className="text-sm">
                            <span className="font-mono text-blue-600 dark:text-blue-400">{variable.name}</span>
                            <span className="text-gray-500"> ({variable.type})</span>
                            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4">
                              {JSON.stringify(variable.value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'quality' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Code Quality Analysis</h3>
              <button
                onClick={analyzeQuality}
                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 flex items-center gap-2"
              >
                <Target className="w-4 h-4" />
                Analyze Quality
              </button>
            </div>

            {qualityMetrics && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
                  <div className="text-center mb-6">
                    <div className={`text-4xl font-bold ${getQualityColor(qualityMetrics.overall)}`}>
                      {qualityMetrics.overall}/100
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      {getQualityIcon(qualityMetrics.overall)}
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Overall Quality Score
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { label: 'Complexity', value: qualityMetrics.complexity, max: 100 },
                      { label: 'Maintainability', value: qualityMetrics.maintainability, max: 100 },
                      { label: 'Testability', value: qualityMetrics.testability, max: 100 },
                      { label: 'Security', value: qualityMetrics.security, max: 100 },
                      { label: 'Performance', value: qualityMetrics.performance, max: 100 }
                    ].map((metric) => (
                      <div key={metric.label} className="text-center">
                        <div className={`text-lg font-semibold ${getQualityColor(metric.value)}`}>
                          {metric.value}
                        </div>
                        <div className="text-xs text-gray-500">{metric.label}</div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                          <div
                            className={`h-2 rounded-full ${metric.value >= 80 ? 'bg-green-500' :
                              metric.value >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${metric.value}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {qualityMetrics.issues.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                    <h4 className="font-medium mb-3">Issues Found ({qualityMetrics.issues.length})</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {qualityMetrics.issues.slice(0, 10).map((issue, index) => (
                        <div key={index} className={`p-3 rounded border ${
                          issue.severity === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
                          issue.severity === 'high' ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800' :
                          issue.severity === 'medium' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800' :
                          'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                        }`}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                              issue.severity === 'critical' ? 'text-red-500' :
                              issue.severity === 'high' ? 'text-orange-500' :
                              issue.severity === 'medium' ? 'text-yellow-500' :
                              'text-blue-500'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium">{issue.category}</span>
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  issue.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                  issue.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                                  issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {issue.severity}
                                </span>
                              </div>
                              <p className="text-sm text-gray-900 dark:text-gray-100">{issue.message}</p>
                              {issue.suggestion && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  💡 {issue.suggestion}
                                </p>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                Line {issue.line}:{issue.column}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {qualityMetrics.suggestions.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Improvement Suggestions</h4>
                    <ul className="space-y-1">
                      {qualityMetrics.suggestions.map((suggestion, index) => (
                        <li key={index} className="text-sm text-green-800 dark:text-green-200 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'refactor' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Code Refactoring</h3>
              <button
                onClick={suggestRefactoring}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Analyze Refactoring
              </button>
            </div>

            {refactorings && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                  <h4 className="font-medium mb-3">Refactoring Opportunities ({refactorings.refactorings.length})</h4>
                  <div className="space-y-3">
                    {refactorings.refactorings.map((refactoring: any, index: number) => (
                      <div key={index} className="border rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium">{refactoring.type}</h5>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              refactoring.impact === 'high' ? 'bg-red-100 text-red-800' :
                              refactoring.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {refactoring.impact} impact
                            </span>
                            <span className="text-sm text-gray-500">
                              {Math.round(refactoring.confidence * 100)}% confidence
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {refactoring.description}
                        </p>
                        <div className="text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded">
                          <div className="text-red-600 dark:text-red-400 mb-1">
                            Before: {refactoring.before}
                          </div>
                          <div className="text-green-600 dark:text-green-400">
                            After: {refactoring.after}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'predict' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Issue Prediction</h3>
              <button
                onClick={handlePredictIssues}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center gap-2"
              >
                <Brain className="w-4 h-4" />
                Predict Issues
              </button>
            </div>

            {predictions && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Risk Assessment</h4>
                    <div className={`px-3 py-1 text-sm rounded ${
                      predictions.riskScore > 70 ? 'bg-red-100 text-red-800' :
                      predictions.riskScore > 40 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      Risk Score: {predictions.riskScore}/100
                    </div>
                  </div>

                  {predictions.predictions.length > 0 && (
                    <div className="space-y-3">
                      <h5 className="font-medium">Predicted Issues:</h5>
                      {predictions.predictions.map((prediction: any, index: number) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="flex items-start gap-2 mb-2">
                            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                              prediction.type === 'security' ? 'text-red-500' :
                              prediction.type === 'performance' ? 'text-yellow-500' :
                              prediction.type === 'maintainability' ? 'text-blue-500' :
                              'text-orange-500'
                            }`} />
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium capitalize">{prediction.type}</span>
                                <span className="text-sm text-gray-500">
                                  {(prediction.probability * 100).toFixed(1)}% probability
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {prediction.description}
                              </p>
                              <p className="text-sm text-green-600 dark:text-green-400">
                                💡 {prediction.prevention}
                              </p>
                              {prediction.similarCases > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Based on {prediction.similarCases} similar cases
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {predictions.recommendations.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Recommendations:</h5>
                      <ul className="space-y-1">
                        {predictions.recommendations.map((rec: string, index: number) => (
                          <li key={index} className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'optimize' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Performance Optimization</h3>
              <button
                onClick={handlePerformanceOptimization}
                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Analyze Performance
              </button>
            </div>

            {performanceAnalysis && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Performance Analysis</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {performanceAnalysis.overallImpact}%
                      </div>
                      <div className="text-sm text-gray-500">Potential Improvement</div>
                    </div>
                  </div>

                  {performanceAnalysis.optimizations.length > 0 && (
                    <div className="space-y-3">
                      <h5 className="font-medium">Optimization Opportunities:</h5>
                      {performanceAnalysis.optimizations.map((opt: any, index: number) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <h6 className="font-medium capitalize">{opt.type} Optimization</h6>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-green-600 font-medium">
                                +{opt.performanceGain}% faster
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${
                                opt.impact === 'high' ? 'bg-red-100 text-red-800' :
                                opt.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {opt.impact} impact
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {opt.description}
                          </p>
                          <div className="text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded">
                            <div className="text-red-600 dark:text-red-400 mb-1">
                              Before: {opt.before}
                            </div>
                            <div className="text-green-600 dark:text-green-400">
                              After: {opt.after}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {performanceAnalysis.recommendations.length > 0 && (
                    <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded">
                      <h5 className="font-medium text-green-900 dark:text-green-100 mb-2">Recommendations:</h5>
                      <ul className="space-y-1">
                        {performanceAnalysis.recommendations.map((rec: string, index: number) => (
                          <li key={index} className="text-sm text-green-800 dark:text-green-200 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Panel for Advanced Features */}
        {showAIPanel && (
          <div className="absolute top-4 right-4 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg border z-50">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">AI Analysis</h3>
                <button
                  onClick={() => setShowAIPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 max-h-96 overflow-y-auto">
              {aiPanelMode === 'predict' && predictions && (
                <div className="space-y-3">
                  <h4 className="font-medium">Issue Predictions</h4>
                  <div className="text-sm">
                    <div className="mb-2">
                      <span className="font-medium">Risk Score:</span>
                      <span className={`ml-2 px-2 py-1 text-xs rounded ${
                        predictions.riskScore > 70 ? 'bg-red-100 text-red-800' :
                        predictions.riskScore > 40 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {predictions.riskScore}/100
                      </span>
                    </div>
                    <div className="space-y-1">
                      {predictions.predictions.slice(0, 3).map((p: any, i: number) => (
                        <div key={i} className="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <div className="font-medium">{p.type.toUpperCase()}</div>
                          <div>{p.description}</div>
                          <div className="text-gray-500">{(p.probability * 100).toFixed(1)}% probability</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {aiPanelMode === 'optimize' && performanceAnalysis && (
                <div className="space-y-3">
                  <h4 className="font-medium">Performance Optimizations</h4>
                  <div className="text-sm">
                    <div className="mb-2">
                      <span className="font-medium">Potential Improvement:</span>
                      <span className="ml-2 text-green-600 font-medium">
                        {performanceAnalysis.overallImpact}%
                      </span>
                    </div>
                    <div className="space-y-1">
                      {performanceAnalysis.optimizations.slice(0, 3).map((opt: any, i: number) => (
                        <div key={i} className="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <div className="font-medium">{opt.type.toUpperCase()}</div>
                          <div>{opt.description}</div>
                          <div className="text-green-600">+{opt.performanceGain}% faster</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}